import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { QuoteRequestState } from '@app/shared';
import { CommodityType } from '@app/shared/enums/commodity-type';
import { QuoteRequestField } from '@app/shared/enums/quote-request-field';
import { Observable, of, Subscription } from 'rxjs';
import { Logger } from '@app/logging.service';
import { Store } from '@ngrx/store';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { ContractLengthRate, QuoteRequestSummary, SupplyDetail } from '@app/quoting/models/quote-request-summary';
import { IPlcNsplRequirement, ResolvePlcNsplRequirement } from '@app/custom/pages/custom-supply-details/plc-nspl-resolver';
import { MatTableDataSource } from '@angular/material/table';
import { SupplyDetails } from '@app/custom/models/supply-details';
import { UdFile } from '@app/filemanagement/UdFile';
import { QuoteRequestStatus } from '@app/shared/models/quote-request-status';
import { AppState } from '@app/state/app.state';
import { isReadOnlySelector } from '@app/state/app.selectors';

@Component({
  selector: 'app-custom-offer',
  templateUrl: './custom-offer.component.html',
  styleUrls: ['./custom-offer.component.scss'],
})
export class CustomOfferComponent implements OnInit, OnDestroy, OnChanges {
  @Input() quoteRequestState: QuoteRequestState;
  @Input() submitEvent: Observable<void>;
  @Input() userIdentity: UtilityHubIdentity;
  @Input() quoteRequestSummary: QuoteRequestSummary;
  @Input() file: UdFile;
  @Input() rateCodes: string[];

  @Output() quoteSubmitted = new EventEmitter<boolean>();

  public quoteForm: FormGroup;
  showMessage: boolean = false;
  public get offerForm(): FormArray {
    return this.quoteForm.get('offers') as FormArray;
  }
  public get offers(): ContractLengthRate[] { return this.quoteRequestSummary?.contractLengthRates };

  private submit$: Subscription;
  private quote$: Subscription;
  public Commodity = CommodityType;
  public QuoteRequestField = QuoteRequestField;

  private logger: Logger;
  public plcNsplRequirement: IPlcNsplRequirement;
  public supplyDetailsSource: MatTableDataSource<SupplyDetail>;
  public contractLengthRatesSource: MatTableDataSource<ContractLengthRate>;
  public displayedUtilityAccColumns: string[] = [];
  public QuoteRequestStatusEnum: QuoteRequestStatus;

  public displayedElectricityUtilityAccColumns: string[] = [
    'utilityAccountNumber',
    'loadProfile',
    'peakLoadContribution',
    'plcLoadFactorPercentage',
    'plcErrors',
    'networkServicePeakLoad',
    'nsplLoadFactorPercentage',
    'nsplErrors',
    'newContractStartDate',
    'meterZipCode',
  ];
  public displayedGasUtilityAccountColumns: string[] = ['utilityAccountNumber', 'loadProfile', 'newContractStartDate', 'meterZipCode'];
  private isReadOnly$: Subscription;
  public isReadOnly: boolean;

  constructor(
    private fb: FormBuilder,
    private quoteRequestService: QuoteRequestService,
    private store: Store<AppState>
  ) {
    this.logger = Logger.for(this);
  }

  ngOnInit(): void {
    this.submit$ = this.submitEvent.subscribe(() => this.save());

    if (!this.quoteForm)
      this.initQuoteForm();

    if (
      this.quoteRequestSummary?.includeSweetSpot && 
      this.quoteRequestSummary?.contractLengthRates.filter(x => x.isSweetSpot === true).length === 0
    ) {
        this.quoteRequestSummary?.contractLengthRates.push({ contractLength: 0, rate: 0, isSweetSpot: true } as ContractLengthRate);
    }
    this.supplyDetailsSource = new MatTableDataSource(this.quoteRequestSummary?.supplyDetails as SupplyDetail[]);
    this.contractLengthRatesSource = new MatTableDataSource(this.quoteRequestSummary?.contractLengthRates as ContractLengthRate[]);
    if (this.quoteRequestSummary?.commodity == CommodityType.Electricity) {
      this.displayedUtilityAccColumns = this.displayedElectricityUtilityAccColumns;
      this.plcNsplRequirement = ResolvePlcNsplRequirement(this.quoteRequestSummary?.iso);
    } else {
      this.displayedUtilityAccColumns = this.displayedGasUtilityAccountColumns;
    }

    this.isReadOnly$ = this.store.select(isReadOnlySelector).subscribe((isReadOnly) => {
      this.isReadOnly = isReadOnly;
      if(isReadOnly) {
        this.quoteForm.disable({ emitEvent: false });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['quoteRequestSummary']?.currentValue) {
      this.quoteRequestSummary = changes['quoteRequestSummary'].currentValue;
      this.supplyDetailsSource = new MatTableDataSource(this.quoteRequestSummary.supplyDetails as any);
      this.contractLengthRatesSource = new MatTableDataSource(this.quoteRequestSummary.contractLengthRates as any);
      if (this.quoteRequestSummary?.commodity == CommodityType.Electricity) {
        this.displayedUtilityAccColumns = this.displayedElectricityUtilityAccColumns;
        this.plcNsplRequirement = ResolvePlcNsplRequirement(this.quoteRequestSummary?.iso);
      } else {
        this.displayedUtilityAccColumns = this.displayedGasUtilityAccountColumns;
      }
      if (!this.quoteForm) {
        this.initQuoteForm();
      }
      this.quoteForm?.get('brokerFee').setValue(this.quoteRequestSummary.brokerFee / 10);
    }
    if (changes['file']) {
      this.file = changes['file'].currentValue;
    }
  }

  ngOnDestroy() {
    this.submit$?.unsubscribe();
    this.quote$?.unsubscribe();
    this.isReadOnly$?.unsubscribe();
  }

  public initOffers() {
    const terms = this.quoteRequestSummary?.contractLengthRates;

    const formGroups: FormGroup[] = [];
    terms.forEach((t) => {
      const group = this.fb.group({
        term: [t, [Validators.required, Validators.min(0), Validators.max(48)]],
        rate: ['', [Validators.required]],
        isSweetSpot: [false],
      });

      formGroups.push(group);
    });

    this.quoteForm = this.fb.group({
      brokerFee: [0, Validators.required],
      offers: this.fb.array(formGroups),
    });

    this.quoteForm?.patchValue(this.quoteRequestSummary);
  }

  public getOfferValues(): Observable<ContractLengthRate[]> {
    return of(this.offerForm?.controls.map((x) => x.get('term').value));
  }

  public initQuoteForm() {
    const formGroups: FormGroup[] = [];
    this.quoteRequestSummary?.contractLengthRates?.forEach((t) => {
      const group = this.fb.group({
        contractLength: [''],
        rate: [''],
        isSweetSpot: [''],
      });
      formGroups.push(group);
    });

    this.quoteForm = this.fb.group({
      brokerFee: [0, Validators.required],
      offers: this.fb.array(formGroups),
    });
    this.quoteForm?.patchValue(this.quoteRequestSummary?.contractLengthRates);
  }

  public isSweetSpot(contractLengthRate: ContractLengthRate): boolean {
    if (this.quoteRequestSummary?.includeSweetSpot) {
      const x = this.contractLengthRatesSource.data[this.contractLengthRatesSource.data.length - 1] === contractLengthRate;
      return x;
    }
    return false;
  }

  getEnergyUnits(): string {
    return this.quoteRequestState.commodity === CommodityType.Gas ? 'therm' : 'kWh';
  }

  submitQuote(): void {
    this.quoteForm.markAllAsTouched();
    if (!this.quoteForm.valid) {
      return;
    }
  }

  calculatePlcLoadFactorPercentage(detail: SupplyDetails) {
    if (!detail.peakLoadContribution || +detail.peakLoadContribution === 0) {
      detail.plcLoadFactorPercentage = 0;
    } else {
      detail.plcLoadFactorPercentage = parseFloat(((10000 / (8760 * detail.peakLoadContribution)) * 100).toFixed(10));
    }
  }

  calculateNsplLoadFactorPercentage(detail: SupplyDetails) {
    if (!detail.networkServicePeakLoad || +detail.networkServicePeakLoad === 0) {
      detail.nsplLoadFactorPercentage = 0;
    } else {
      detail.nsplLoadFactorPercentage = (10000 / (8760 * detail.networkServicePeakLoad)) * 100;
    }
  }

  getPlcFullValidationMessageStr(data: SupplyDetail) {
    if (!this.isPlcBelowCustomerThreshold(data) && data.peakLoadContribution !== undefined) {
      return `${this.plcNsplRequirement.plcLabel} cannot exceed 100%. Please change the plc selection.`;
    }
  }

  getNsplFullValidationMessageStr(data: SupplyDetail) {
    if (!this.isNsplBelowCustomerThreshold(data) && data.peakLoadContribution !== undefined) {
      return `NSPL Load Factor cannot exceed 100%. Please change the NSPL selection.`;
    }
  }

  isPlcBelowCustomerThreshold(data: SupplyDetail): boolean {
    return data.plcLoadFactorPercentage && data.plcLoadFactorPercentage <= 100;
  }

  isNsplBelowCustomerThreshold(data: SupplyDetail): boolean {
    return data.nsplLoadFactorPercentage && data.nsplLoadFactorPercentage <= 100;
  }

  private validateUtilityAccountData(): boolean {
    let valid = false;

    if (this.quoteRequestSummary?.commodity !== CommodityType.Gas) {
      this.quoteRequestSummary?.supplyDetails.forEach((supplyDetail) => {
        const plcValid = this.isPlcBelowCustomerThreshold(supplyDetail) || !this.plcNsplRequirement.plc;
        const nsplValid = this.isNsplBelowCustomerThreshold(supplyDetail) || !this.plcNsplRequirement.nspl;

        valid = plcValid && nsplValid;
      });
    } else {
      return true;
    }

    return valid;
  }

  private validateContractLengthRates(): boolean {
    // all contract lengths have rates and sweetspot contract length is not 0
    return (
      this.contractLengthRatesSource.data.length === this.contractLengthRatesSource.data.filter(clr => clr.rate > 0).length && 
      this.contractLengthRatesSource.data.length === this.contractLengthRatesSource.data.filter(clr => clr.contractLength > 0).length
    );
  }

  setRate(sd: any, eventData: any) {
    if (sd.rate == 0 || sd.rat == '') {
      this.showMessage = true;
    }
    sd.rate = eventData.srcElement.value;
  }

  setContractLength(sd: any, eventData: any) {
    sd.contractLength = eventData.srcElement.value;
    sd.isSweetSpot = true;
  }

  save() {
    this.quoteForm.markAllAsTouched();
    if (this.quoteForm.valid && this.validateUtilityAccountData() && this.validateContractLengthRates()) {

      this.quoteRequestSummary.contractLengthRates = this.contractLengthRatesSource.data;

      this.quoteRequestService.saveQuoteRequestSummary(this.quoteRequestSummary)
        .subscribe((x) => {
          this.quoteSubmitted.emit(true);
          return of(true);
        });
    }
  }
}
